/* eslint-disable max-len */

import { FC, MouseEvent, useMemo, useState } from 'react';
import { Row, Tooltip, Typography, Popconfirm, Col } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import TaskInlineTimerButton from 'components/Timer/TaskInlineTimerButton';
import StatusTag from 'components/StatusTag';
import UserAvatarPopover from 'components/UserAvatarPopover';
import apiRoutes from 'config/apiRoute';
import apiRequests from 'utils/api';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import { convertMinsToHrsMins, parseDateToUSFormat, renderTime } from 'utils/string.utils';
import style from './index.module.less';

const { Title, Text } = Typography;

interface RequestTaskCardProps {
  requestUuid: string;
  task: any;
  listTasks: any[];
  disableSubtaskAlignment?: boolean;
  onRemoved: () => void;
}

const RequestTaskCard: FC<RequestTaskCardProps> = ({
  requestUuid,
  task,
  listTasks,
  onRemoved,
  disableSubtaskAlignment = false,
}) => {
  const [removing, setRemoving] = useState(false);
  const responsible = task.related_users.find((el: any) => el.pivot.type === 'responsible');

  const totalTimeSubTasks = useMemo((): number => {
    const getTasksTotalTime = (subtasks: any[]): number => {
      return subtasks.reduce<number>(
        (acc, subtask: any) =>
          acc +
          (listTasks.find((task: any) => task.uuid === subtask.uuid) ? subtask.total_timelogs ?? 0 : 0) +
          getTasksTotalTime(subtask.subtasks),
        0,
      );
    };

    return getTasksTotalTime(task.subtasks);
  }, [listTasks, task]);

  const removeClickHandler = async (event: MouseEvent<HTMLElement> | undefined) => {
    event?.stopPropagation();

    setRemoving(true);

    try {
      await apiRequests.put(`${apiRoutes.PROJECT_TASKS}/${task.uuid}`, { remove_requests: [requestUuid] });

      onRemoved();
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <Link
        to={`?task_uuid=${task.uuid}`}
        className={style.card}
        style={{
          borderLeftColor: `var(--priority-${task.task_priority}-color)`,
          marginBottom: 8,
          display: 'block',
        }}
      >
        <Row wrap={false} justify="space-between" align="middle">
          <Title level={5} className="no-margin">
            {task.task_title}
          </Title>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} style={{ marginBottom: 4, display: 'flex', alignItems: 'center' }}>
              <TaskInlineTimerButton taskId={task.uuid} taskStatus={task.task_status} relatedUsers={task.related_users} fontSize={20} />
            </span>

            <Popconfirm
            disabled={removing}
            title="Do you want to remove this task from the request?"
            onConfirm={removeClickHandler}
            onCancel={(event) => event?.stopPropagation()}
          >
            <Tooltip title="Remove" placement="left">
              <CloseCircleOutlined className="red pointer" style={{ fontSize: 20, marginBottom: 4 }} />
            </Tooltip>
          </Popconfirm>
          </div>
        </Row>

        <Row gutter={[8, 8]} justify="space-between">
          <Col>
            <Text type="secondary">{task.project_title}</Text>
          </Col>
          <Col>
            <StatusTag size="small" value={task.task_status} />
          </Col>
        </Row>

        <Row justify="space-between" style={{ paddingTop: 4 }}>
          {responsible ? (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <UserAvatarPopover size="small" user={responsible} style={{ width: '24px' }} />
            </div>
          ): '-'}

          <span>M: {renderTime(convertMinsToHrsMins(task.total_timelogs ?? 0))}</span>

          <span style={{ visibility: !totalTimeSubTasks ? 'hidden' : undefined }}>
            S: {renderTime(convertMinsToHrsMins(totalTimeSubTasks))}
          </span>

          <span>{parseDateToUSFormat(task.task_created_at)}</span>
        </Row>
      </Link>

      {task.subtasks && task.subtasks.length > 0 && (
        <div
          style={{
            paddingLeft: disableSubtaskAlignment ? 0 : 20,
            marginLeft: disableSubtaskAlignment ? 0 : 10,
            display: 'block',
          }}
        >
          {task.subtasks.map((subtask: any) => (
            <RequestTaskCard
              key={subtask.uuid}
              task={subtask}
              requestUuid={requestUuid}
              listTasks={listTasks}
              onRemoved={onRemoved}
              disableSubtaskAlignment
            />
          ))}
        </div>
      )}
    </>
  );
};

export default RequestTaskCard;
